import React, { useEffect, useMemo, useRef } from "react";
import { useGLTF } from "@react-three/drei";
import { RigidBody } from "@react-three/rapier";
import * as THREE from "three";
import { useUI } from "../../stores/useUI";
import type { Object as ObjectType } from "../../types/MapData.types";
import {
  OUTLINE_ENABLED,
  OUTLINE_THICKNESS,
  OUTLINE_COLOR,
  OUTLINE_OPACITY,
  GROUND_LINE_ENABLED,
  GROUND_LINE_THICKNESS,
  GROUND_LINE_OPACITY,
} from "../../config/game";
import { computeSmoothedNormals } from "../../utils/computeSmoothedNormals";

// Shared outline material for all instanced objects
const sharedOutlineMaterial = OUTLINE_ENABLED
  ? new THREE.ShaderMaterial({
      side: THREE.BackSide,
      transparent: true,
      uniforms: {
        outlineThickness: { value: OUTLINE_THICKNESS },
        outlineColor: { value: OUTLINE_COLOR },
        outlineOpacity: { value: OUTLINE_OPACITY },
      },
      vertexShader: `
        attribute vec3 smoothNormal;
        uniform float outlineThickness;
        void main() {
          #ifdef USE_INSTANCING
            mat4 mvMatrix = viewMatrix * modelMatrix * instanceMatrix;
          #else
            mat4 mvMatrix = modelViewMatrix;
          #endif
          vec4 viewPos = mvMatrix * vec4(position, 1.0);
          vec3 viewNormal = normalize(mat3(mvMatrix) * smoothNormal);
          float dist = -viewPos.z;
          viewPos.xyz += viewNormal * outlineThickness * dist * 0.001;
          gl_Position = projectionMatrix * viewPos;
        }
      `,
      fragmentShader: `
        uniform vec3 outlineColor;
        uniform float outlineOpacity;
        void main() {
          gl_FragColor = vec4(outlineColor, outlineOpacity);
        }
      `,
    })
  : null;

interface SubMeshData {
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  localMatrix: THREE.Matrix4;
  localPosition: THREE.Vector3;
  localQuaternion: THREE.Quaternion;
  localScale: THREE.Vector3;
}

interface InstancedObjectGroupProps {
  modelURL: string;
  objects: ObjectType[];
  mapId: string;
  layoutTextureUrl?: string;
  groundY: number;
  physicsTypes: string[];
}

export default function InstancedObjectGroup({
  modelURL,
  objects,
  mapId,
  layoutTextureUrl,
  groundY,
  physicsTypes,
}: InstancedObjectGroupProps) {
  const markItemLoaded = useUI((s) => s.markItemLoaded);
  const gltf = useGLTF(modelURL);
  const hasMarkedLoaded = useRef(false);

  // Mark this model URL as loaded (1 call per unique modelURL, matching registerMapDataItems)
  useEffect(() => {
    if (gltf && !hasMarkedLoaded.current) {
      hasMarkedLoaded.current = true;
      markItemLoaded();
    }
  }, [gltf, markItemLoaded]);

  useEffect(() => {
    useGLTF.preload(modelURL);
  }, [modelURL]);

  useEffect(() => {
    return () => {
      useGLTF.clear(modelURL);
    };
  }, [modelURL]);

  const layoutTexture = useMemo(() => {
    if (!layoutTextureUrl) return null;
    const texture = new THREE.TextureLoader().load(layoutTextureUrl);
    texture.flipY = false;
    return texture;
  }, [layoutTextureUrl]);

  // Extract sub-meshes with their local transforms
  const subMeshes = useMemo<SubMeshData[]>(() => {
    if (!gltf?.scene) return [];
    const scene = gltf.scene;
    scene.updateMatrixWorld(true);
    const sceneInverse = new THREE.Matrix4()
      .copy(scene.matrixWorld)
      .invert();
    const result: SubMeshData[] = [];

    scene.traverse((child: any) => {
      if (!child.isMesh || !child.geometry) return;
      if (!child.geometry.getAttribute("smoothNormal")) {
        computeSmoothedNormals(child.geometry);
      }
      const localMatrix = new THREE.Matrix4().multiplyMatrices(
        sceneInverse,
        child.matrixWorld
      );
      const localPosition = new THREE.Vector3();
      const localQuaternion = new THREE.Quaternion();
      const localScale = new THREE.Vector3();
      localMatrix.decompose(localPosition, localQuaternion, localScale);
      result.push({
        geometry: child.geometry,
        material: child.material,
        localMatrix,
        localPosition,
        localQuaternion,
        localScale,
      });
    });
    return result;
  }, [gltf]);


  // Build instance matrices from objects
  const instanceMatrices = useMemo(() => {
    const tempPos = new THREE.Vector3();
    const tempQuat = new THREE.Quaternion();
    const tempScale = new THREE.Vector3();
    const objMatrix = new THREE.Matrix4();

    return subMeshes.map((sm) => {
      const matrices: THREE.Matrix4[] = [];
      for (const obj of objects) {
        tempPos.set(obj.position[0], obj.position[1], obj.position[2]);
        if (obj.quaternion) {
          tempQuat.set(
            obj.quaternion[0],
            obj.quaternion[1],
            obj.quaternion[2],
            obj.quaternion[3]
          );
        } else {
          tempQuat.identity();
        }
        tempScale.set(obj.scale[0], obj.scale[1], obj.scale[2]);
        objMatrix.compose(tempPos, tempQuat, tempScale);
        const full = objMatrix.clone().multiply(sm.localMatrix);
        matrices.push(full);
      }
      return matrices;
    });
  }, [subMeshes, objects]);

  // Create InstancedMeshes
  const instanceGroups = useMemo(() => {
    if (subMeshes.length === 0) return [];
    const count = objects.length;

    return subMeshes.map((sm, meshIdx) => {
      const matrices = instanceMatrices[meshIdx];

      // Main material
      const mainMat = sm.material.clone();
      if (layoutTexture) (mainMat as any).map = layoutTexture;
      (mainMat as any).depthWrite = true;
      mainMat.needsUpdate = true;

      const mainIM = new THREE.InstancedMesh(sm.geometry, mainMat, count);
      mainIM.castShadow = true;
      mainIM.receiveShadow = true;
      mainIM.frustumCulled = false;

      // Outline
      let outlineIM: THREE.InstancedMesh | null = null;
      if (OUTLINE_ENABLED && sharedOutlineMaterial) {
        outlineIM = new THREE.InstancedMesh(
          sm.geometry,
          sharedOutlineMaterial,
          count
        );
        outlineIM.renderOrder = 1;
        outlineIM.frustumCulled = false;
      }

      // Intersection
      let intersectionIM: THREE.InstancedMesh | null = null;
      if (GROUND_LINE_ENABLED) {
        const intMat = new THREE.ShaderMaterial({
          transparent: true,
          depthWrite: false,
          side: THREE.DoubleSide,
          polygonOffset: true,
          polygonOffsetFactor: -1,
          polygonOffsetUnits: -1,
          uniforms: {
            groundY: { value: groundY },
            lineColor: { value: OUTLINE_COLOR },
            lineOpacity: { value: GROUND_LINE_OPACITY },
            groundLineThickness: { value: GROUND_LINE_THICKNESS },
          },
          vertexShader: `
            varying vec3 vWorldPos;
            void main() {
              #ifdef USE_INSTANCING
                vec4 worldPos = modelMatrix * instanceMatrix * vec4(position, 1.0);
              #else
                vec4 worldPos = modelMatrix * vec4(position, 1.0);
              #endif
              vWorldPos = worldPos.xyz;
              gl_Position = projectionMatrix * viewMatrix * worldPos;
            }
          `,
          fragmentShader: `
            uniform float groundY;
            uniform vec3 lineColor;
            uniform float lineOpacity;
            uniform float groundLineThickness;
            varying vec3 vWorldPos;
            void main() {
              float dist = abs(vWorldPos.y - groundY);
              float fw = fwidth(vWorldPos.y);
              float threshold = max(fw * groundLineThickness, 0.001);
              if (dist > threshold) discard;
              gl_FragColor = vec4(lineColor, lineOpacity);
            }
          `,
        });
        intersectionIM = new THREE.InstancedMesh(
          sm.geometry,
          intMat,
          count
        );
        intersectionIM.frustumCulled = false;
      }

      // Set instance matrices
      for (let i = 0; i < count; i++) {
        mainIM.setMatrixAt(i, matrices[i]);
        if (outlineIM) outlineIM.setMatrixAt(i, matrices[i]);
        if (intersectionIM) intersectionIM.setMatrixAt(i, matrices[i]);
      }
      mainIM.instanceMatrix.needsUpdate = true;
      if (outlineIM) outlineIM.instanceMatrix.needsUpdate = true;
      if (intersectionIM) intersectionIM.instanceMatrix.needsUpdate = true;

      return { main: mainIM, outline: outlineIM, intersection: intersectionIM };
    });
  }, [subMeshes, instanceMatrices, layoutTexture, groundY, objects.length]);

  // Physics objects — use CuboidCollider (no scene cloning)
  const physicsObjects = useMemo(
    () =>
      objects.filter((obj) =>
        physicsTypes.includes(obj.type.toLowerCase())
      ),
    [objects, physicsTypes]
  );

  if (!gltf?.scene || instanceGroups.length === 0) return null;

  return (
    <>
      {/* Instanced visual rendering */}
      {instanceGroups.map((group, i) => (
        <React.Fragment key={i}>
          <primitive object={group.main} />
          {group.outline && <primitive object={group.outline} />}
          {group.intersection && <primitive object={group.intersection} />}
        </React.Fragment>
      ))}

      {/* Physics colliders — hull from shared geometry (no scene cloning) */}
      {physicsObjects.map((obj) => {
        const q = obj.quaternion
          ? new THREE.Quaternion(
              obj.quaternion[0],
              obj.quaternion[1],
              obj.quaternion[2],
              obj.quaternion[3]
            )
          : undefined;
        return (
          <RigidBody
            key={obj.id}
            type="fixed"
            position={obj.position}
            colliders="hull"
            includeInvisible
          >
            <group scale={obj.scale} quaternion={q}>
              {subMeshes.map((sm, i) => (
                <mesh
                  key={i}
                  geometry={sm.geometry}
                  position={sm.localPosition}
                  quaternion={sm.localQuaternion}
                  scale={sm.localScale}
                  visible={false}
                >
                  <meshBasicMaterial />
                </mesh>
              ))}
            </group>
          </RigidBody>
        );
      })}
    </>
  );
}
